import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';
import { StandardEvents } from '@shopify/events';

const PRODUCT_CARD_IMAGE_SELECTOR = '.product-media__image';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  /** @type {WeakMap<HTMLImageElement, string>} */
  #defaultImageSizes = new WeakMap();

  #gridObserver = new MutationObserver(() => this.#syncProductCardImageSizes());

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    document.addEventListener(StandardEvents.collectionUpdate, this.#handleCatalogUpdate);
    document.addEventListener('scroll', this.#handleCatalogScroll, { capture: true, passive: true });
    this.#observeGrid();
    this.#setupProgressiveCatalog();
    this.setAttribute('initialized', '');
  }

  updatedCallback() {
    super.updatedCallback();
    this.#observeGrid();
    this.#setupProgressiveCatalog();
  }

  #visibleProducts = 24;

  /** @type {IntersectionObserver | undefined} */
  #catalogObserver;

  /** @type {Element | undefined} */
  #catalogBoundary;

  #catalogScrollFrame = 0;

  // Fast scrolling can jump over an IntersectionObserver target between frames.
  #handleCatalogScroll = () => {
    if (!this.#catalogBoundary || this.#catalogScrollFrame) return;
    this.#catalogScrollFrame = requestAnimationFrame(() => {
      this.#catalogScrollFrame = 0;
      if (!this.isConnected || !this.#catalogBoundary) return;
      const bounds = this.#catalogBoundary.getBoundingClientRect();
      if (bounds.width > 0 && bounds.top <= window.innerHeight + 400) this.#revealMoreProducts();
    });
  };

  // Reapply progressive disclosure after the native section render has settled.
  // A morph can preserve this component while replacing only its nested grid.
  #handleCatalogUpdate = async (event) => {
    if (!this.dataset.ingaPageSize || !this.contains(event.target)) return;
    try {
      await event.promise;
      if (!this.isConnected) return;
      super.updatedCallback();
      this.#observeGrid();
      this.#setupProgressiveCatalog();
    } catch {
      // Keep the current usable grid if the native filter request fails.
    }
  };

  #setupProgressiveCatalog() {
    this.#catalogObserver?.disconnect();
    this.#catalogObserver = undefined;
    this.#catalogBoundary = undefined;
    if (!this.dataset.ingaPageSize) return;
    const pageSize = Number(this.dataset.ingaPageSize);
    this.#visibleProducts = pageSize;
    const cards = Array.from(this.refs.grid?.querySelectorAll(':scope > li[data-product-id]') || []);
    const hashIndex = cards.findIndex(card => `#${card.id}` === location.hash);
    if (hashIndex >= this.#visibleProducts) this.#visibleProducts = Math.ceil((hashIndex + 1) / pageSize) * pageSize;
    if (this.dataset.ingaAutoLoad !== 'false' && typeof IntersectionObserver !== 'undefined') {
      this.#catalogObserver = new IntersectionObserver((entries) => {
        if (!this.isConnected) return;
        if (entries.some(entry => entry.isIntersecting && entry.target === this.#catalogBoundary)) {
          this.#revealMoreProducts();
        }
      }, { rootMargin: '0px 0px 400px 0px' });
    }
    this.#updateProgressiveCatalog();
  }

  #updateProgressiveCatalog() {
    const cards = Array.from(this.refs.grid?.querySelectorAll(':scope > li[data-product-id]') || []);
    cards.forEach((card, index) => { card.hidden = index >= this.#visibleProducts; });
    this.#catalogObserver?.disconnect();
    this.#catalogBoundary = undefined;
    if (this.#catalogObserver && this.#visibleProducts < cards.length) {
      // Observe the last visible card so each new batch establishes a fresh boundary.
      this.#catalogBoundary = cards[this.#visibleProducts - 1];
      if (this.#catalogBoundary) this.#catalogObserver.observe(this.#catalogBoundary);
    }
    const controls = this.querySelector('[data-inga-load-more]');
    if (!controls) return;
    controls.hidden = false;
    const counter = controls.querySelector('[data-inga-count]');
    if (counter) counter.textContent = counter.dataset.countTemplate.replace('__COUNT__', String(Math.min(this.#visibleProducts, cards.length)));
    const button = controls.querySelector('button');
    if (button) button.hidden = Boolean(this.#catalogObserver) || this.#visibleProducts >= cards.length;
  }

  #revealMoreProducts() {
    this.#visibleProducts += Number(this.dataset.ingaPageSize || 24);
    this.#updateProgressiveCatalog();
  }

  /** Reveal the next native product cards without losing filter or sort state. */
  showMoreProducts() {
    const firstNewIndex = this.#visibleProducts;
    this.#revealMoreProducts();
    const cards = this.refs.grid?.querySelectorAll(':scope > li[data-product-id]');
    cards?.[firstNewIndex]?.querySelector('a')?.focus({ preventScroll: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    document.removeEventListener(StandardEvents.collectionUpdate, this.#handleCatalogUpdate);
    document.removeEventListener('scroll', this.#handleCatalogScroll, true);
    cancelAnimationFrame(this.#catalogScrollFrame);
    this.#catalogScrollFrame = 0;
    this.#gridObserver.disconnect();
    this.#catalogObserver?.disconnect();
    this.#catalogObserver = undefined;
    this.#catalogBoundary = undefined;
    this.#defaultImageSizes = new WeakMap();
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;

    grid.setAttribute('product-grid-view', value);
    this.#syncProductCardImageSizes();
  }

  #observeGrid() {
    this.#gridObserver.disconnect();

    const { grid } = this.refs;
    if (!grid) return;

    this.#gridObserver.observe(grid, { childList: true, subtree: true });
    this.#syncProductCardImageSizes();
  }

  #syncProductCardImageSizes = () => {
    const { grid } = this.refs;
    if (!grid) return;

    const useSingleColumnSize = !mediaQueryLarge.matches && grid.getAttribute('product-grid-view') === 'mobile-single';
    const images = /** @type {NodeListOf<HTMLImageElement>} */ (grid.querySelectorAll(PRODUCT_CARD_IMAGE_SELECTOR));

    for (const image of images) {
      if (!this.#defaultImageSizes.has(image)) this.#defaultImageSizes.set(image, image.sizes);

      const sizes = useSingleColumnSize ? '100vw' : this.#defaultImageSizes.get(image);
      if (sizes !== undefined && image.sizes !== sizes) image.sizes = sizes;
    }
  };

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
